<script setup lang="ts">
/* ===========================================================================
   ui-delta — ThemeToggle
   ---------------------------------------------------------------------------
   ADDS:      The light/dark switch. Sits in the app bar, left of the account.
   UPSTREAM:  No equivalent in @ui.
   AT MERGE:  Move into @ui with theme.css and useTheme.
   ORIGIN:    elfie-design guide, components/layouts/ThemeToggle.jsx.
   =========================================================================== */
import { computed } from 'vue'
import { IconMoon, IconSun } from '@tabler/icons-vue'
import { useTheme } from './useTheme'

interface Props {
  /** Set when placed on the navy app bar, so it borrows field colours. */
  onField?: boolean
  size?: number
}
const props = withDefaults(defineProps<Props>(), { onField: false, size: 18 })

const { resolved, toggle } = useTheme()
// The glyph states which theme is showing — sun in light, moon in dark. The
// action lives in the label, so the icon is never a puzzle.
const label = computed(() => (resolved.value === 'dark' ? 'Switch to light' : 'Switch to dark'))
</script>

<template>
  <button
    type="button"
    :class="['theme-toggle', { 'theme-toggle--on-field': props.onField }]"
    :aria-label="label"
    :title="label"
    @click="toggle"
  >
    <component :is="resolved === 'dark' ? IconMoon : IconSun" :size="props.size" />
  </button>
</template>

<style scoped>
.theme-toggle {
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* A ringed circle, matching the account pill beside it — a bare glyph on the
     navy reads as an afterthought rather than a control. */
  border: 1px solid var(--line-on-field);
  border-radius: var(--radius-full);
  background: transparent;
  color: var(--fg-tertiary);
  cursor: pointer;
  transition:
    background-color 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.theme-toggle:hover {
  background: var(--surface-hover);
  color: var(--fg-primary);
}

/* On the navy field the toggle has to read against a dark ground in both
   themes, so it borrows the field's own foreground rather than the page's. */
.theme-toggle--on-field {
  color: var(--fg-on-field);
  border-color: var(--line-on-field);
}

.theme-toggle--on-field:hover {
  background: var(--surface-field-raised);
  border-color: var(--fg-on-field-muted);
}
</style>
